import { useState, useMemo, useEffect, useRef } from 'react';
import type { CharacterBenefits } from '../../hooks/useCharacterBenefits';
import type { PlaySession, CustomEffect } from '../../stores/playSessionStore';
import { usePlaySessionStore } from '../../stores/playSessionStore';
import { useDiceRollerStore } from '../../stores/diceRollerStore';
import type { ConditionName } from '@character-creator/types';
import { useCombatFeatures } from '../../hooks/useCombatFeatures';
import { useCombatLayout } from '../../hooks/useCombatLayout';
import { CombatLayoutMartial } from './CombatLayoutMartial';
import { CombatLayoutCaster } from './CombatLayoutCaster';
import { CombatLeftPanel } from './combat/CombatLeftPanel';
import { categorizeFeatures } from '@character-creator/core';

interface Attack {
  name: string;
  bonus: string;
  damage: string;
  damageType: string;
}

interface SpellLists {
  cantrips: string[];
  knownSpells: string[];
  preparedSpells: string[];
}

interface PlayModeCombatProps {
  benefits: CharacterBenefits;
  session: PlaySession | null;
  attacks: Attack[];
  spells: SpellLists;
  onDamage: () => void;
  onHeal: () => void;
  onAddTempHp: () => void;
  onToggleCondition: (condition: ConditionName) => void;
  onDeathSaveSuccess: () => void;
  onDeathSaveFailure: () => void;
  onResetDeathSaves: () => void;
  onUseHitDie: (dieType: string) => number | null;
  onUseResource: (name: string, amount?: number) => void;
  onRestoreResource: (name: string, amount?: number) => void;
  onUseSpellSlot: (level: string) => void;
  onRestoreSpellSlot: (level: string) => void;
  onSetConcentration: (spell: string | null) => void;
  onSetSneakAttackEnabled: (enabled: boolean) => void;
  bardicInspiration: boolean;
  dmInspiration: boolean;
  bardLevel: number;
  customEffects: CustomEffect[];
  onToggleBardicInspiration: () => void;
  onToggleDmInspiration: () => void;
  onAddCustomEffect: (name: string, description: string) => void;
  onRemoveCustomEffect: (id: string) => void;
}

export function PlayModeCombat({
  benefits,
  session,
  attacks,
  spells,
  onDamage,
  onHeal,
  onAddTempHp,
  onToggleCondition,
  onDeathSaveSuccess,
  onDeathSaveFailure,
  onResetDeathSaves,
  onUseHitDie,
  onUseResource,
  onRestoreResource,
  onUseSpellSlot,
  onRestoreSpellSlot,
  onSetConcentration,
  onSetSneakAttackEnabled: _onSetSneakAttackEnabled,
  bardicInspiration,
  dmInspiration,
  bardLevel,
  customEffects,
  onToggleBardicInspiration,
  onToggleDmInspiration,
  onAddCustomEffect,
  onRemoveCustomEffect,
}: PlayModeCombatProps) {
  // Unused props for future use
  void _onSetSneakAttackEnabled;

  const combatFeatures = useCombatFeatures(benefits);
  const [layoutTransitioning, setLayoutTransitioning] = useState(false);
  const [healingResult, setHealingResult] = useState<{
    die: string;
    roll: number;
    conMod: number;
    total: number;
  } | null>(null);

  // Track timeout for cleanup
  const healingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    const timeoutRef = healingTimeoutRef.current;
    return () => {
      if (timeoutRef) clearTimeout(timeoutRef);
    };
  }, []);

  // Get layout preference from session store
  const layoutPreference = session?.layoutPreference ?? 'auto';

  // Combine all spell IDs for detection
  const allSpellIds = useMemo(() => [
    ...spells.cantrips,
    ...spells.knownSpells,
    ...spells.preparedSpells,
  ], [spells]);

  // Detect combat layout
  const { layout } = useCombatLayout(
    attacks.length,
    allSpellIds,
    session?.spellSlots,
    layoutPreference
  );

  // Track previous layout for transitions
  const prevLayoutRef = useRef(layout);

  // Handle layout transitions
  useEffect(() => {
    if (prevLayoutRef.current !== layout) {
      setLayoutTransitioning(true);
      const timer = setTimeout(() => {
        setLayoutTransitioning(false);
        prevLayoutRef.current = layout;
      }, 200); // 200ms fade transition

      return () => clearTimeout(timer);
    }
  }, [layout]);

  // Crit auto-detect: watch dice roller for nat 20 on attack rolls
  const lastRollLabel = useDiceRollerStore((s) => s.lastRollLabel);
  const lastRollWasNat20 = useDiceRollerStore((s) => s.lastRollWasNat20);
  const setCritActive = usePlaySessionStore((s) => s.setCritActive);

  useEffect(() => {
    if (lastRollLabel === null) return;
    if (lastRollLabel.endsWith('Attack')) {
      setCritActive(lastRollWasNat20);
    } else if (lastRollLabel.endsWith('Damage')) {
      setCritActive(false);
    }
  }, [lastRollLabel, lastRollWasNat20, setCritActive]);

  const currentHp = session?.currentHp ?? benefits.hitPoints;
  const maxHp = session?.maxHp ?? benefits.hitPoints;
  const tempHp = session?.tempHp ?? 0;
  const isDying = currentHp === 0;

  // Categorize class features into bonus actions, reactions, and other
  const categorizedActions = useMemo(
    () => categorizeFeatures(benefits.classFeatures),
    [benefits.classFeatures]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 relative">
      <CombatLeftPanel
        benefits={benefits}
        session={session}
        currentHp={currentHp}
        maxHp={maxHp}
        tempHp={tempHp}
        isDying={isDying}
        healingResult={healingResult}
        onDamage={onDamage}
        onHeal={onHeal}
        onAddTempHp={onAddTempHp}
        onDeathSaveSuccess={onDeathSaveSuccess}
        onDeathSaveFailure={onDeathSaveFailure}
        onResetDeathSaves={onResetDeathSaves}
        onUseHitDie={onUseHitDie}
        onUseResource={onUseResource}
        onRestoreResource={onRestoreResource}
        onToggleCondition={onToggleCondition}
        onSetHealingResult={setHealingResult}
        healingTimeoutRef={healingTimeoutRef}
        bardicInspiration={bardicInspiration}
        dmInspiration={dmInspiration}
        bardLevel={bardLevel}
        customEffects={customEffects}
        onToggleBardicInspiration={onToggleBardicInspiration}
        onToggleDmInspiration={onToggleDmInspiration}
        onAddCustomEffect={onAddCustomEffect}
        onRemoveCustomEffect={onRemoveCustomEffect}
      />

      {/* Right Panel - Actions */}
      <div className="space-y-4">
        {/* Layout Container with Fade Transition */}
        <div className={`transition-opacity duration-200 ${layoutTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          {layout === 'combat-priority' ? (
            <CombatLayoutMartial
              benefits={benefits}
              session={session}
              attacks={attacks}
              spells={spells}
              categorizedActions={categorizedActions}
              combatFeatures={combatFeatures}
              onUseSpellSlot={onUseSpellSlot}
              onRestoreSpellSlot={onRestoreSpellSlot}
              onSetConcentration={onSetConcentration}
            />
          ) : (
            <CombatLayoutCaster
              benefits={benefits}
              session={session}
              attacks={attacks}
              spells={spells}
              categorizedActions={categorizedActions}
              combatFeatures={combatFeatures}
              onUseSpellSlot={onUseSpellSlot}
              onRestoreSpellSlot={onRestoreSpellSlot}
              onSetConcentration={onSetConcentration}
            />
          )}
        </div>
      </div>
    </div>
  );
}
